import { Box, Skeleton, Text } from '@factorialco/deprecated-design-system'
import {
  CustomCategoryForSelectFragment,
  CustomCategorySelectorDocument,
  CustomCategorySelectorQuery,
} from 'generated/resources/graphql'

import { EntitySelector } from 'modules/core/components/EntitySelector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import type {
  CustomCategorySelectorMultipleProps,
  CustomCategorySelectorSingleProps,
} from './types'

const isSingleProps = (
  props: CustomCategorySelectorSingleProps | CustomCategorySelectorMultipleProps
): props is CustomCategorySelectorSingleProps => {
  return !props.multiple
}

const Item = ({ category }: { category: CustomCategoryForSelectFragment }) => {
  return (
    <Box gap="s8" flexDirection="row" alignItems="center" key={category.id}>
      <Text.Normal size="300">{category.label}</Text.Normal>
    </Box>
  )
}

export const CustomCategorySelector = (
  props: CustomCategorySelectorSingleProps | CustomCategorySelectorMultipleProps
) => {
  const {
    error,
    mandatory,
    label,
    width,
    minWidth,
    emptyLabel,
    alwaysOpen = false,
    disabled = false,
    type = 'all',
    level = 'all',
    statuses = ['enabled'],
  } = props

  const renderEntity = (entity: CustomCategoryForSelectFragment) => <Item category={entity} />

  const renderSkeleton = () => {
    return (
      <Box gap="s8" flexDirection="row" alignItems="center">
        <Skeleton width="s240" height="s32" />
      </Box>
    )
  }

  const variables = {
    companyId: useCurrentSession().company.id,
    categoryLevel: level,
    type,
    statuses,
  }

  const extractConnection = (data: CustomCategorySelectorQuery) => {
    return data.finance.customCategoriesConnection
  }

  // We need this, otherwise TypeScript doesn't know how to build the
  // types for the EntitySelector
  const filterEntities = (entities: CustomCategoryForSelectFragment[]) => entities

  const document = CustomCategorySelectorDocument

  const renderSingleEntity = (entity: CustomCategoryForSelectFragment | null) => {
    return (
      <Text.Normal>
        {entity !== null
          ? entity.label
          : emptyLabel ??
            i18n.tp('finance.custom_categories_selector.select_category', { count: 1 })}
      </Text.Normal>
    )
  }

  const renderMultipleEntities = (entities: CustomCategoryForSelectFragment[]) => {
    return (
      <Text.Normal>
        {entities.length > 0
          ? entities.map((entity) => entity.label).join(', ')
          : emptyLabel ??
            i18n.tp('finance.custom_categories_selector.select_category', { count: 0 })}
      </Text.Normal>
    )
  }

  if (!isSingleProps(props)) {
    return (
      <EntitySelector.Multiple
        label={label}
        disabled={disabled}
        error={error}
        mandatory={mandatory}
        alwaysOpen={alwaysOpen}
        document={document}
        variables={variables}
        width={width}
        minWidth={minWidth}
        extractConnection={extractConnection}
        renderEntity={renderEntity}
        value={props.value ?? []}
        onChange={props.onSelect}
        renderSkeleton={renderSkeleton}
        renderValue={renderMultipleEntities}
        filterEntities={filterEntities}
      />
    )
  }

  return (
    <EntitySelector
      label={label}
      disabled={disabled}
      error={error}
      mandatory={mandatory}
      document={document}
      variables={variables}
      width={width}
      minWidth={minWidth}
      extractConnection={extractConnection}
      renderEntity={renderEntity}
      value={props.value ?? null}
      onChange={props.onSelect}
      renderSkeleton={renderSkeleton}
      renderValue={renderSingleEntity}
      filterEntities={filterEntities}
    />
  )
}
